import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useRBAC } from "@/hooks/use-rbac";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  useChecklistTemplates,
  useUpdateChecklistTemplate,
} from "@/hooks/use-checklist-templates";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import type {
  ChecklistTemplateItem,
  DBChecklistTemplate,
  MeetingType,
} from "@/lib/db-types";

const MEETING_LABELS: Record<MeetingType, string> = {
  startup: "Oppstartsmøte",
  status: "Statusmøte",
  debrief: "Debrief",
};

const MEETING_ORDER: MeetingType[] = ["startup", "status", "debrief"];

function genKey(): string {
  return `item_${Math.random().toString(36).slice(2, 10)}`;
}

interface TemplateEditorProps {
  template: DBChecklistTemplate;
  onSave: (items: ChecklistTemplateItem[]) => Promise<void>;
  saving: boolean;
}

function TemplateEditor({ template, onSave, saving }: TemplateEditorProps) {
  const [items, setItems] = useState<ChecklistTemplateItem[]>(template.items);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setItems(template.items);
    setDirty(false);
  }, [template.items, template.id]);

  const updateItem = (idx: number, label: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, label } : it)));
    setDirty(true);
  };

  const addItem = () => {
    setItems((prev) => [...prev, { key: genKey(), label: "" }]);
    setDirty(true);
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const moveItem = (idx: number, direction: -1 | 1) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const reset = () => {
    setItems(template.items);
    setDirty(false);
  };

  const handleSave = async () => {
    const trimmed = items
      .map((it) => ({ ...it, label: it.label.trim() }))
      .filter((it) => it.label.length > 0);
    if (trimmed.length === 0) {
      toast.error("Minst ett sjekklistepunkt kreves");
      return;
    }
    await onSave(trimmed);
    setDirty(false);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg">{MEETING_LABELS[template.meeting_type]}</h2>
            <p className="text-xs text-muted-foreground">
              Sist oppdatert {formatDate(template.updated_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <Button size="sm" variant="ghost" onClick={reset} disabled={saving}>
                Nullstill
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? "Lagrer..." : "Lagre"}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={item.key} className="flex items-center gap-2 group">
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Flytt opp"
                >
                  <SrIcon name="chevron-up" className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Flytt ned"
                >
                  <SrIcon name="chevron-down" className="w-3.5 h-3.5" />
                </button>
              </div>
              <Input
                value={item.label}
                onChange={(e) => updateItem(idx, e.target.value)}
                placeholder="Beskrivelse av sjekklistepunktet..."
                className="flex-1 text-sm"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeItem(idx)}
                className="text-muted-foreground hover:text-destructive"
                aria-label="Fjern punkt"
              >
                <SrIcon name="x" className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <Button size="sm" variant="outline" onClick={addItem} className="w-full">
          <SrIcon name="plus" className="w-4 h-4 mr-1" />
          Legg til punkt
        </Button>

        <p className="text-xs text-muted-foreground border-l-2 border-muted pl-3">
          Endringer påvirker kun nye møter fra og med lagring. Eksisterende møter
          beholder sin opprinnelige sjekkliste.
        </p>
      </CardContent>
    </Card>
  );
}

export default function ChecklistTemplatesPage() {
  const { isAdmin } = useRBAC();
  const { activeUser } = useImpersonation();
  const { data: templates = [], isLoading } = useChecklistTemplates();
  const updateMutation = useUpdateChecklistTemplate();

  if (!isAdmin) return <Navigate to="/" replace />;

  const handleSave = async (meetingType: MeetingType, items: ChecklistTemplateItem[]) => {
    try {
      await updateMutation.mutateAsync({
        meetingType,
        items,
        updatedBy: activeUser?.id ?? null,
      });
      toast.success(`${MEETING_LABELS[meetingType]} oppdatert`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Ukjent feil";
      toast.error(`Kunne ikke lagre: ${message}`);
    }
  };

  const sorted = MEETING_ORDER.map((type) => templates.find((t) => t.meeting_type === type)).filter(
    (t): t is DBChecklistTemplate => t !== undefined,
  );

  return (
    <div className="page-container animate-fade-in">
      <div className="flex items-center gap-2 mb-2">
        <SrIcon name="check-circle2" className="w-6 h-6 text-primary" />
        <h1 className="font-display text-2xl">Møte-sjekklister</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        System-defaults for sjekklister i oppstartsmøte, statusmøte og debrief.
        Endringer gjelder bare for nye møter fra og med lagring. Eksisterende
        møter beholder sin opprinnelige sjekkliste.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Ingen templates funnet. Sjekk at seed-migrasjonen har kjørt.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 max-w-2xl">
          {sorted.map((template) => (
            <TemplateEditor
              key={template.id}
              template={template}
              onSave={(items) => handleSave(template.meeting_type, items)}
              saving={updateMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}
